// ⚠️ FB-AUTHORIZED SYNTHETIC DEMO CLINICAL STORE (Slack Jun 18) — THU-346 (Slice 3, Prescribe). The
// cockpit Prescribe interface, persisted per patient to the demo clinical store via the local
// prescriptions client. MOCK prescribe: rows persist to the demo store only and are NEVER transmitted to
// any pharmacy / e-prescribing network — see the "not transmitted" affordance below. Synthetic data
// only; NOT the production system of record; see DECISIONS.md (D5.1).
//
// ⚠️ This file legitimately carries prescribing terms (the Prescribe action + form labels) and is
// therefore explicitly allowlisted in scripts/check-clinical-terms.mjs. The rest of the app stays gated —
// do not copy these strings elsewhere without the same scoped carve-out.
import { useState } from "react";
import { Modal } from "@championcare/ui";
import { usePrescriptionsApi, type Prescription } from "../lib/prescriptionsApi";
import { useAsync } from "../lib/useAsync";

const XIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// `open`/`onClose` are controlled by the parent so the Prescribe trigger can live in the hero (top of
// the page, left of Open EMR) while this module/list stays in the Information column.
export function PrescribePanel({
  patientId,
  open,
  onClose,
}: {
  patientId: string;
  open: boolean;
  onClose: () => void;
}) {
  const api = usePrescriptionsApi();
  const { data, loading, error } = useAsync(() => api.list(patientId), [patientId]);
  const [items, setItems] = useState<Prescription[] | null>(null);
  const list = items ?? data ?? [];

  if (items === null && data) setItems(data);

  async function add(rx: { drug: string; dosage: string; sig: string; notes: string }) {
    const created = await api.create(patientId, rx);
    setItems((prev) => [...(prev ?? data ?? []), created]);
  }
  async function remove(id: string) {
    setItems((prev) => (prev ?? data ?? []).filter((r) => r.id !== id));
    try {
      await api.remove(patientId, id);
    } catch {
      setItems(null);
    }
  }

  return (
    <div className="pp-info-block pp-rx-block">
      <h4>
        Prescriptions <span className="pp-count">({list.length})</span>
      </h4>

      {error ? (
        <span className="pp-info-text muted">Couldn&rsquo;t load right now.</span>
      ) : loading && items === null ? (
        <span className="pp-info-text muted">Loading…</span>
      ) : list.length ? (
        <div className="pp-rx-list">
          {list.map((rx) => (
            <div className="pp-rx" key={rx.id}>
              <div className="pp-rx-main">
                <span className="pp-rx-drug">{rx.drug}</span>
                {rx.dosage ? <span className="pp-rx-dose">{rx.dosage}</span> : null}
              </div>
              {rx.sig ? <div className="pp-rx-sig">{rx.sig}</div> : null}
              <button
                type="button"
                className="pp-rx-rm"
                aria-label={`Remove ${rx.drug}`}
                onClick={() => void remove(rx.id)}
              >
                {XIcon}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <span className="pp-info-text muted">No active prescriptions</span>
      )}

      {open && <PrescribeModal onClose={onClose} onSubmit={add} />}
    </div>
  );
}

function PrescribeModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (rx: { drug: string; dosage: string; sig: string; notes: string }) => Promise<void>;
}) {
  const [drug, setDrug] = useState("");
  const [dosage, setDosage] = useState("");
  const [sig, setSig] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const valid = drug.trim().length > 0;

  async function submit() {
    if (!valid || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({ drug: drug.trim(), dosage: dosage.trim(), sig: sig.trim(), notes: notes.trim() });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn’t save");
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="prescribeTitle">
      <div className="block-modal-title" id="prescribeTitle">
        Prescribe <em>medication</em>
      </div>
      <div className="block-modal-sub">Add a prescription to this patient&rsquo;s demo chart.</div>

      {err && <div className="pp-rx-err">{err}</div>}

      <div className="block-field">
        <label htmlFor="rxDrug">Drug</label>
        <input
          id="rxDrug"
          value={drug}
          onChange={(e) => setDrug(e.target.value)}
          placeholder="e.g. Amoxicillin"
          maxLength={300}
          autoFocus
        />
      </div>
      <div className="block-field">
        <label htmlFor="rxDosage">Dosage</label>
        <input
          id="rxDosage"
          value={dosage}
          onChange={(e) => setDosage(e.target.value)}
          placeholder="e.g. 500 mg"
          maxLength={300}
        />
      </div>
      <div className="block-field">
        <label htmlFor="rxSig">Sig (patient instructions)</label>
        <textarea
          id="rxSig"
          value={sig}
          onChange={(e) => setSig(e.target.value)}
          placeholder="e.g. 1 capsule by mouth three times daily for 7 days"
          rows={2}
        />
      </div>
      <div className="block-field">
        <label htmlFor="rxNotes">Notes (optional)</label>
        <input
          id="rxNotes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal note"
          maxLength={300}
        />
      </div>

      <div className="block-foot">
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={() => void submit()} disabled={!valid || busy}>
          {busy ? "Saving…" : "Add prescription"}
        </button>
      </div>
    </Modal>
  );
}
